// 功能归因公共方案

import logger from '@/utils/client-logger';
import { getSession, removeSession, setSession } from './tools';

// 类型定义
interface RecommendUsedParams {
  recommend_position_type?: string;
  email_template?: string;
  is_recommend_server?: boolean;
  shop_sku?: string;
  start_time?: number;
}

interface RecommendUsedData {
  recommend_position_type?: string;
  email_template?: string;
  is_recommend_server?: boolean;
  shop_sku?: string;
  start_time?: number;
}

interface PreScanUsedParams {
  pre_scan_scene_type?: string;
}

interface PreScanUsedData {
  pre_scan_scene_type?: string;
}

interface TryOnUsedParams {
  entrance_tryon_page_name?: string;
  entrance_tryon_scene?: string;
}

interface TryOnUsedData {
  entrance_tryon_page_name?: string;
  entrance_tryon_scene?: string;
}

interface AccFeatureValueParams {
  item?: {
    recall_group_type?: string;
    fine_ranking_type?: string;
  };
  recommend_position_type?: string;
}

interface AccFeatureValueUsed {
  add_to_cart_flags: string[];
  recommend_position_type?: string;
  is_recommend_server: boolean;
}

interface FeatureValueUsed {
  add_to_cart_flags: string[];
  recommend_position_type?: string;
  email_template?: string;
  is_recommend_server?: boolean;
  pre_scan_scene_type?: string;
  entrance_tryon_page_name?: string;
  entrance_tryon_scene?: string;
}

interface FeatureValueItem {
  from_type: string;
  from_pub_name: string;
  recommend_position_type?: string;
  email_template?: string;
  is_recommend_server?: boolean;
  pre_scan_scene_type?: string;
  entrance_tryon_page_name?: string;
  entrance_tryon_scene?: string;
}

interface BlockFlagsData {
  block_flags?: string;
  block_flags_ext?: string;
  fine_ranking_type?: string;
  recall_group_type?: string;
  product_index?: number;
  first_picture_url?: string;
  cart_item_shop_sku_list?: string[] | null;
  cart_item_product_id_list?: string[] | null;
  refer_page_position?: string;
}

// 工具函数
const isArray = (value: any): value is any[] => Array.isArray(value);

// =============== 推荐功能相关 ===============

/**
 * 功能入口-推荐
 * @param recommend_position_type {string} 推荐版位类型，可选值参考路由中的 feature_value_name
 * @param is_recommend_server {boolean} 是否是推荐系统的推荐，true:推荐系统推荐商品，false:其他推荐
 * @param shop_sku {string} 商品SKU
 */
export function addRecommendUsed(params: RecommendUsedParams = {}): void {
  const { recommend_position_type, email_template, is_recommend_server, shop_sku, start_time } =
    params;

  setSession('featureValue__recommend', {
    recommend_position_type,
    email_template,
    is_recommend_server,
    shop_sku,
    start_time,
  });
}

/**
 * 获取推荐功能使用信息
 * @param {string} [shop_sku] - 商品SKU，可选参数。如果传入则会校验session中的shop_sku是否一致
 * @returns {Object} 返回推荐相关字段
 * @returns {string} recommend_position_type - 推荐版位类型，可选值参考路由中的 feature_value_name
 * @returns {boolean} is_recommend_server - 是否是推荐系统推荐，true:推荐系统推荐商品，false:其他推荐
 * @returns {string} shop_sku - 商品SKU
 */
export function getRecommendUsed(shop_sku?: string): Partial<RecommendUsedData> {
  const sessionData = getSession('featureValue__recommend') as RecommendUsedData | null;
  if (!sessionData) return {};

  if (shop_sku && sessionData.shop_sku && sessionData.shop_sku !== shop_sku) {
    return {};
  }
  // 30分钟内有效. 目前只有邮件的商品推荐有这个限制
  if (sessionData.start_time && Date.now() - sessionData.start_time > 1000 * 60 * 30) {
    return {};
  }

  const result = { ...sessionData };
  delete result.shop_sku;
  delete result.start_time;
  return result;
}

export function removeRecommendUsed(): void {
  const recommendUsed = getRecommendUsed();
  // 邮件过来的商品推荐不主动打断（只有30分钟内有效这1个条件）
  if (recommendUsed.recommend_position_type !== 'email_recommend') {
    removeSession('featureValue__recommend');
  }
}

/**
 * 获取配件功能使用信息（神策）
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 商品对象
 * @param {string} options.item.recall_group_type - 召回组类型
 * @param {string} options.item.fine_ranking_type - 精排类型
 * @param {string} options.recommend_position_type - 推荐位类型
 * @returns {Object} 返回功能使用相关字段
 * @returns {Array} add_to_cart_flags - 加购标记数组,包含 'from_recommend' 等标记
 * @returns {string} recommend_position_type - 推荐位类型
 * @returns {boolean} is_recommend_server - 是否是推荐系统推荐,true:推荐系统推荐商品,false:其他推荐
 */
export function getAccFeatureValueUsed(
  params: AccFeatureValueParams = {},
): Partial<AccFeatureValueUsed> {
  try {
    const { item, recommend_position_type } = params;
    return {
      add_to_cart_flags: ['from_recommend'],
      recommend_position_type,
      is_recommend_server: !!(item?.recall_group_type || item?.fine_ranking_type),
    };
  } catch (error) {
    logger.error(error);
  }
  return {};
}

/**
 * 获取配件功能使用信息(业务)
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 商品对象
 * @param {string} options.item.recall_group_type - 召回组类型
 * @param {string} options.item.fine_ranking_type - 精排类型
 * @param {string} options.recommend_position_type - 推荐位类型
 * @returns {Array} 返回功能使用相关字段数组
 */
export function getAccFeatureValueUsedList(params: AccFeatureValueParams = {}): FeatureValueItem[] {
  try {
    const { item, recommend_position_type } = params;
    // 饰品加购归因，则把镜框归因给清掉
    removeSession('featureValue__recommend');
    const featureValue = getAccFeatureValueUsed({ item, recommend_position_type });
    if (featureValue.recommend_position_type) {
      return [
        {
          from_type: 'from_recommend',
          recommend_position_type: featureValue.recommend_position_type,
          is_recommend_server: featureValue.is_recommend_server || false,
          from_pub_name: 'Web',
        },
      ];
    }
  } catch (error) {
    logger.error(error);
  }
  return [];
}

// =============== 处方识别功能相关 ===============

/**
 * 功能入口-处方识别
 * @param pre_scan_scene_type {string} 处方识别过程类型，可选值：direct_use、cache_use、save_load_use
 */
export function addPreScanUsed(params: PreScanUsedParams = {}): void {
  const { pre_scan_scene_type } = params;
  setSession('featureValue__pre_scan', { pre_scan_scene_type });
}

export function getPreScanUsed(): PreScanUsedData {
  return (getSession('featureValue__pre_scan') as PreScanUsedData) || {};
}

export function removePreScanUsed(): void {
  removeSession('featureValue__pre_scan');
}

// =============== 试戴功能相关 ===============

export function addTryOnUsed(params: TryOnUsedParams): void {
  const { entrance_tryon_page_name, entrance_tryon_scene } = params;
  setSession('featureValue__try_on', { entrance_tryon_page_name, entrance_tryon_scene });
}

export function getTryOnUsed(): TryOnUsedData {
  return (getSession('featureValue__try_on') as TryOnUsedData) || {};
}

export function removeTryOnUsed(): void {
  removeSession('featureValue__try_on');
}

// =============== 综合功能相关 ===============

/**
 * 获取功能使用信息（神策）
 * @param {string} shop_sku - 商品SKU
 * @returns {Object} 返回功能使用相关字段
 * @returns {Array} add_to_cart_flags - 加购标记数组,包含 'from_recommend' 等标记
 */
export function getFeatureValueUsed(shop_sku?: string): Partial<FeatureValueUsed> {
  try {
    const featureValue: FeatureValueUsed = {
      add_to_cart_flags: [],
    };

    const recommendUsed = getRecommendUsed(shop_sku);
    if (recommendUsed.recommend_position_type) {
      featureValue.add_to_cart_flags.push('from_recommend');
      Object.assign(featureValue, recommendUsed);
    }

    const preScanUsed = getPreScanUsed();
    if (preScanUsed.pre_scan_scene_type) {
      featureValue.add_to_cart_flags.push('from_pre_scan');
      Object.assign(featureValue, preScanUsed);
    }

    const tryOnUsed = getTryOnUsed();
    if (tryOnUsed.entrance_tryon_page_name) {
      featureValue.add_to_cart_flags.push('from_tryon');
      Object.assign(featureValue, tryOnUsed);
    }

    if (featureValue.add_to_cart_flags.length) {
      return featureValue;
    }
  } catch (error) {
    logger.error(error);
  }
  return {};
}

/**
 * 获取功能使用信息(业务)
 * @param {string} shop_sku - 商品SKU
 * @returns {Array} 返回功能使用相关字段数组
 */
export function getFeatureValueUsedList(shop_sku?: string): FeatureValueItem[] {
  try {
    const featureValues: FeatureValueItem[] = [];

    const recommendUsed = getRecommendUsed(shop_sku);
    if (recommendUsed.recommend_position_type) {
      featureValues.push({
        from_type: 'from_recommend',
        from_pub_name: 'Web',
        recommend_position_type: recommendUsed.recommend_position_type,
        email_template: recommendUsed.email_template,
        is_recommend_server: recommendUsed.is_recommend_server || false,
      });
    }

    const preScanUsed = getPreScanUsed();
    if (preScanUsed.pre_scan_scene_type) {
      featureValues.push({
        from_type: 'from_pre_scan',
        from_pub_name: 'Web',
        pre_scan_scene_type: preScanUsed.pre_scan_scene_type,
        is_recommend_server: false,
      });
    }

    const tryOnUsed = getTryOnUsed();
    if (tryOnUsed.entrance_tryon_page_name) {
      featureValues.push({
        from_type: 'from_tryon',
        from_pub_name: 'Web',
        entrance_tryon_page_name: tryOnUsed.entrance_tryon_page_name,
        entrance_tryon_scene: tryOnUsed.entrance_tryon_scene,
        is_recommend_server: false,
      });
    }

    return featureValues;
  } catch (error) {
    logger.error(error);
  }
  return [];
}

/**
 * 删除所有功能使用信息
 */
export function removeAllFeatureValueUsed(): void {
  removeRecommendUsed();
  removePreScanUsed();
  removeTryOnUsed();
}
// =============== 商品详情页相关BI数据 ===============

// 存储商品详情页的所有BI信息
export function setBlockFlags(flagsData: Partial<BlockFlagsData>): void {
  // 将所有flags合并到一个对象中
  const detailData: BlockFlagsData = {
    block_flags: flagsData.block_flags || '',
    block_flags_ext: flagsData.block_flags_ext || '',
    fine_ranking_type: flagsData.fine_ranking_type || '',
    recall_group_type: flagsData.recall_group_type || '',
    product_index: flagsData.product_index,
    first_picture_url: flagsData.first_picture_url || '',
    cart_item_shop_sku_list: isArray(flagsData.cart_item_shop_sku_list)
      ? flagsData.cart_item_shop_sku_list
      : null,
    cart_item_product_id_list: isArray(flagsData.cart_item_product_id_list)
      ? flagsData.cart_item_product_id_list
      : null,
    refer_page_position: flagsData.refer_page_position || '',
  };

  // 将对象序列化后存储在单一键下
  setSession('z_goods_detail_block_flags_data', detailData);
}

// 获取商品详情页的所有BI信息
export function getBlockFlags(): BlockFlagsData {
  try {
    // 获取存储的JSON字符串并解析
    const detailData = getSession('z_goods_detail_block_flags_data') as BlockFlagsData | null;

    // 存储当前数据作为前一个页面的数据，便于page_leave使用
    if (detailData) {
      setSession('z_goods_detail_block_flags_data_prev', detailData);
    }

    if (!detailData) {
      return {
        block_flags: '',
        block_flags_ext: '',
        fine_ranking_type: '',
        recall_group_type: '',
        product_index: undefined,
        first_picture_url: '',
        cart_item_shop_sku_list: null,
        cart_item_product_id_list: null,
        refer_page_position: '',
      };
    }

    return {
      block_flags: detailData.block_flags || '',
      block_flags_ext: detailData.block_flags_ext || '',
      fine_ranking_type: detailData.fine_ranking_type || '',
      recall_group_type: detailData.recall_group_type || '',
      product_index: detailData.product_index,
      first_picture_url: detailData.first_picture_url || '',
      cart_item_shop_sku_list: isArray(detailData.cart_item_shop_sku_list)
        ? detailData.cart_item_shop_sku_list
        : null,
      cart_item_product_id_list: isArray(detailData.cart_item_product_id_list)
        ? detailData.cart_item_product_id_list
        : null,
      refer_page_position: detailData.refer_page_position || '',
    };
  } catch (error) {
    logger.error(error);
    return {
      block_flags: '',
      block_flags_ext: '',
      fine_ranking_type: '',
      recall_group_type: '',
      product_index: undefined,
      first_picture_url: '',
      cart_item_shop_sku_list: null,
      cart_item_product_id_list: null,
      refer_page_position: '',
    };
  }
}

// 获取前一个页面的所有BI信息(page leave时使用)
export function getBlockFlagsPrev(): BlockFlagsData {
  try {
    const detailData = getSession('z_goods_detail_block_flags_data_prev') as BlockFlagsData | null;
    return (
      detailData || {
        block_flags: '',
        block_flags_ext: '',
        fine_ranking_type: '',
        recall_group_type: '',
        product_index: undefined,
        first_picture_url: '',
        cart_item_shop_sku_list: null,
        cart_item_product_id_list: null,
        refer_page_position: '',
      }
    );
  } catch (error) {
    logger.error(error);
    return {
      block_flags: '',
      block_flags_ext: '',
      fine_ranking_type: '',
      recall_group_type: '',
      product_index: undefined,
      first_picture_url: '',
      cart_item_shop_sku_list: null,
      cart_item_product_id_list: null,
      refer_page_position: '',
    };
  }
}

// 删除商品详情页的所有BI信息
export function removeBlockFlags(): void {
  removeSession('z_goods_detail_block_flags_data');
  removeSession('z_goods_detail_block_flags_data_prev');
}

// 获取商品详情页的前置group信息
export function getGroupInfo(): { group_id: string; group_name: string } {
  if (typeof window === 'undefined') {
    return {
      group_id: '',
      group_name: '',
    };
  }
  const blockFlags = sessionStorage.getItem('z_goods_detail_group_info') as
    | string
    | null
    | undefined;
  if (!blockFlags) {
    return {
      group_id: '',
      group_name: '',
    };
  }
  try {
    return JSON.parse(blockFlags) as { group_id: string; group_name: string };
  } catch (error) {
    logger.error(error);
    return {
      group_id: '',
      group_name: '',
    };
  }
}

export function setGroupInfo(groupInfo: { group_id: string; group_name: string }): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem('z_goods_detail_group_info', JSON.stringify(groupInfo));
}

export function removeGroupInfo(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem('z_goods_detail_group_info');
}

// 获取当前商品详情页的商品信息
export function getProductInfo(): { product_sku: string; shop_sku: string } {
  if (typeof window === 'undefined') {
    return {
      product_sku: '',
      shop_sku: '',
    };
  }
  const productInfo = sessionStorage.getItem('z_goods_detail_product_info') as
    | string
    | null
    | undefined;
  if (!productInfo) {
    return {
      product_sku: '',
      shop_sku: '',
    };
  }
}

export function setProductInfo(productInfo: { product_sku: string; shop_sku: string }): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem('z_goods_detail_product_info', JSON.stringify(productInfo));
}

export function removeProductInfo(): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem('z_goods_detail_product_info');
}
